import { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { TitlingLender } from '@makemydeal/dr-platform-types';
import * as lenderActionCreators from '@makemydeal/dr-dash-store/actions/lenderActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { LenderAddressValidationContext } from './lenderAddressValidationContext';

type LenderAddressFieldKey = keyof Pick<TitlingLender, 'line1' | 'line2' | 'city' | 'state' | 'postalCode' | 'phone'>;

/**
 * Reads the current titling lender and returns a setter that soft-saves a per-field merge via
 * updateTitlingLenderWithAddress. When a `field` is provided and a LenderAddressValidationProvider
 * is present, also returns that field's inline `error` and an `onFieldValidation` change handler
 * (sourced from context so callers don't drill validation props).
 *
 * Also records the merged address as this scenario's edit for the current lender, so a manually corrected
 * field is reused instead of being overwritten by a re-fetch on the next lender re-selection.
 */
export const useLenderAddressField = (field?: LenderAddressFieldKey) => {
    const dispatch = useDispatch();
    const lenderAddress = useSelector(offerReduxSelectors.getTitlingLenderWithAddress);
    const lenderId = useSelector(offerReduxSelectors.getCreditDecisionLender)?.id;

    const updateField = useCallback(
        (fieldKey: LenderAddressFieldKey, value: string | null) => {
            const nextLender: TitlingLender = { ...(lenderAddress ?? {}), [fieldKey]: value };
            dispatch(lenderActionCreators.updateTitlingLenderWithAddress(nextLender));
            // This case won't be possible, put the condition for type safety
            if (lenderId) {
                dispatch(lenderActionCreators.updateTitlingAddressCache(lenderId, nextLender));
            }
        },
        [dispatch, lenderAddress, lenderId]
    );

    const validation = useContext(LenderAddressValidationContext);
    const error = field ? validation?.getFieldError(field) : undefined;
    const onFieldValidation = field && validation ? (value: string) => validation.onFieldValidation(field, value) : undefined;

    return { lenderAddress, updateField, error, onFieldValidation };
};
